import {useEffect, useState} from 'react';
import Header from '../layout/Header';
import {getMaxDeeplink, getTelegramDeeplink, getYandexClientId} from '../../utils/telegram';

// Animated typing dots
function TypingIndicator() {
    return (
        <div className="flex items-center gap-1 px-4 py-3">
            <div className="w-2 h-2 rounded-full bg-brand-dark/40 animate-bounce" style={{animationDelay: '0ms'}}/>
            <div className="w-2 h-2 rounded-full bg-brand-dark/40 animate-bounce" style={{animationDelay: '150ms'}}/>
            <div className="w-2 h-2 rounded-full bg-brand-dark/40 animate-bounce" style={{animationDelay: '300ms'}}/>
        </div>
    );
}

// Chat message bubble
function ChatBubble({message, isBot, delay = 0, showTyping = false}) {
    const [visible, setVisible] = useState(false);
    const [typing, setTyping] = useState(showTyping);

    useEffect(() => {
        const typingTimer = setTimeout(() => {
            setTyping(false);
        }, delay);

        const visibleTimer = setTimeout(() => {
            setVisible(true);
        }, delay + (showTyping ? 800 : 0));

        return () => {
            clearTimeout(typingTimer);
            clearTimeout(visibleTimer);
        };
    }, [delay, showTyping]);

    if (typing) {
        return (
            <div className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                <div className="bg-white rounded-2xl shadow-sm">
                    <TypingIndicator/>
                </div>
            </div>
        );
    }

    if (!visible) return null;

    return (
        <div
            className={`flex ${isBot ? 'justify-start' : 'justify-end'} animate-fade-in-up`}
        >
            <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                    isBot
                        ? 'bg-white text-brand-dark'
                        : 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white'
                }`}
            >
                <p className="text-[15px] leading-relaxed whitespace-pre-line">{message}</p>
            </div>
        </div>
    );
}

// Bot avatar with gradient ring
function BotAvatar() {
    return (
        <div className="relative">
            <div
                className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-red via-brand-pink to-brand-orange p-[3px]">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                    <span className="text-3xl">🎬</span>
                </div>
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"/>
        </div>
    );
}

export default function TelegramLanding() {
    const [yandexClientId, setYandexClientId] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [headingIndex, setHeadingIndex] = useState(0);
    const [headingVisible, setHeadingVisible] = useState(true);
    const headings = ['Видео-поздравление с ИИ', 'Песня-поздравление с ИИ'];

    // Set SEO meta tags
    useEffect(() => {
        document.title = 'Telegram бот для видеопоздравлений от ИИ-звёзд | Selebrus';
        document.querySelector('meta[name="description"]')?.setAttribute(
            'content',
            'Создавайте видеопоздравления от ИИ-звёзд прямо в Telegram. Быстро и удобно — выберите звезду, напишите текст и получите готовое видео в чате.'
        );
    }, []);

    useEffect(() => {
        getYandexClientId().then(setYandexClientId);
        // Trigger animations after mount
        const timer = setTimeout(() => setIsLoaded(true), 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setHeadingVisible(false);
            setTimeout(() => {
                setHeadingIndex(i => (i + 1) % headings.length);
                setHeadingVisible(true);
            }, 300);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const telegramLink = getTelegramDeeplink(yandexClientId);
    const maxLink = getMaxDeeplink(yandexClientId);

    return (
        <div className="min-h-screen bg-white font-sans overflow-hidden">
            {/* Decorative background elements */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-brand-red/5 via-brand-pink/5 to-brand-orange/5 blur-3xl"/>
                <div
                    className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-brand-orange/5 via-brand-pink/5 to-brand-red/5 blur-3xl"/>
            </div>

            <div className="relative z-10">
                <Header onHomeClick={() => window.location.href = '/'}/>

                <main className="max-w-[1110px] mx-auto px-4 py-8 md:py-16">
                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

                        {/* Left side - Content */}
                        <div
                            className={`flex-1 text-center lg:text-left transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                        >

                            <h1 className="text-[36px] md:text-[52px] font-bold text-brand-dark leading-[1.1] tracking-[-0.02em] mb-6">
                                <span
                                    className={`inline-block transition-all duration-300 ${headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                                    {headings[headingIndex]}
                                </span>{' '}
                                <span className="text-gradient">прямо в мессенджере</span>
                            </h1>

                            <p className="text-lg md:text-xl text-brand-dark/70 leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                                Быстро и удобно — выбирай звезду, пиши текст и получи готовое видеопоздравление от
                                звезды или песню в чате
                            </p>

                            <div
                                className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                                <a
                                    href={telegramLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white font-semibold text-lg rounded-2xl shadow-lg shadow-brand-red/25 hover:shadow-xl hover:shadow-brand-red/30 hover:scale-[1.02] transition-all duration-300"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path
                                            d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                    </svg>
                                    Открыть бота
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                         className="group-hover:translate-x-1 transition-transform">
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </a>

                                <a
                                    href={maxLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#6c3ce1] text-white font-semibold text-lg rounded-2xl shadow-lg shadow-[#6c3ce1]/25 hover:shadow-xl hover:shadow-[#6c3ce1]/30 hover:scale-[1.02] transition-all duration-300"
                                >
                                    Открыть бота в макс
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                         className="group-hover:translate-x-1 transition-transform">
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </a>

                                <a
                                    href="/"
                                    className="group inline-flex items-center gap-2 px-8 py-4 border-2 border-brand-dark/15 text-brand-dark font-semibold text-lg rounded-2xl hover:border-brand-dark/30 hover:scale-[1.02] transition-all duration-300"
                                >
                                    Перейти на сайт
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                         className="group-hover:translate-x-1 transition-transform">
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </a>

                            </div>

                            {/* Features */}
                            <div
                                className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-10 text-sm text-brand-dark/60">
                                <div className="flex items-center gap-2">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" className="text-green-500">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round"
                                              strokeLinejoin="round"/>
                                        <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Видеопоздравление за 3 минуты
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" className="text-green-500">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round"
                                              strokeLinejoin="round"/>
                                        <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    10+ звёзд
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" className="text-green-500">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round"
                                              strokeLinejoin="round"/>
                                        <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Более 1000 поздравленных
                                </div>
                            </div>
                        </div>

                    </div>
                </main>
            </div>

            {/* Custom animation styles */}
            <style>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.4s ease-out forwards;
                }
            `}</style>
        </div>
    );
}
